import { useEffect, useState } from 'react';
import { authApi } from '../api';
import { useAuth } from '../AuthContext';
import { IconCamera, IconChevronDown, IconCheck, IconKeypad, IconLink, IconCopy, IconSave } from '../components/icons';
import { ErrorNote, Modal, Spinner, useLoader } from '../components/common';

/**
 * โปรไฟล์ — the account page behind the header menu.
 *
 * The password and the PIN are never shown, only replaced: each opens its own
 * dialog and neither is part of the บันทึก form, so a half-finished name edit
 * can never carry a credential change with it.
 */
export default function Profile() {
  const { refresh } = useAuth();
  const profile = useLoader(() => authApi.profile(), []);

  const [form, setForm] = useState(null);
  const [dialog, setDialog] = useState(null);     // 'password' | 'pin'
  const [lineOpen, setLineOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile.data) return;
    const p = profile.data.profile;
    setForm({
      username: p.username || '',
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      email: p.email || '',
      phone: p.phone || '',
      avatarUrl: p.avatarUrl || '',
    });
  }, [profile.data]);

  if (profile.loading || !form) return <Spinner />;
  if (profile.error) return <ErrorNote error={profile.error} />;

  const line = profile.data.profile.line;
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setSaved(false); };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await authApi.updateProfile(form);
      await refresh();
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1 className="page-title">โปรไฟล์</h1>

      <div className="card profile-card">
        <ErrorNote error={error} />

        <div className="profile-top">
          <label className="avatar-upload">
            {form.avatarUrl
              ? <img src={form.avatarUrl} alt="" />
              : <IconCamera size={26} />}
            <span className="avatar-badge"><IconCamera size={13} /></span>
            <input
              type="url"
              hidden
              value={form.avatarUrl}
              onChange={set('avatarUrl')}
            />
          </label>

          <div className="profile-fields">
            <div className="grid-3">
              <label className="field">
                <span>User Id<i>*</i></span>
                <input value={form.username} onChange={set('username')} />
              </label>
              <label className="field">
                <span>ชื่อจริง<i>*</i></span>
                <input value={form.firstName} onChange={set('firstName')} />
              </label>
              <label className="field">
                <span>นามสกุล<i>*</i></span>
                <input value={form.lastName} onChange={set('lastName')} />
              </label>
            </div>
          </div>
        </div>

        <div className="grid-2">
          <label className="field">
            <span>อีเมล<i>*</i></span>
            <input type="email" value={form.email} onChange={set('email')} />
          </label>
          <label className="field">
            <span>เบอร์โทรศัพท์<i>*</i></span>
            <input value={form.phone} onChange={set('phone')} />
          </label>
        </div>

        <div className="grid-2">
          <div className="field">
            <span>รหัสผ่าน</span>
            <button className="btn-lilac" onClick={() => setDialog('password')}>
              <IconKeypad size={16} /> เปลี่ยนรหัสผ่าน
            </button>
          </div>
          <div className="field">
            <span>รหัส PIN</span>
            <button className="btn-lilac" onClick={() => setDialog('pin')}>
              <IconKeypad size={16} /> {profile.data.profile.hasPin ? 'เปลี่ยนรหัส PIN' : 'ตั้งรหัส PIN'}
            </button>
          </div>
        </div>

        <div className="field">
          <span>สถานะเชื่อมต่อการแจ้งเตือนกับ LINE</span>
          <button className="line-status" onClick={() => setLineOpen((v) => !v)}>
            {line.connected
              ? <><i className="ok"><IconCheck size={13} /></i> เชื่อมต่อสำเร็จแล้ว</>
              : <><i className="off" /> ยังไม่ได้เชื่อมต่อ</>}
            <IconChevronDown size={17} className={lineOpen ? 'flip' : ''} />
          </button>
        </div>

        {lineOpen && (
          <div className="line-howto">
            <strong>วิธีการเชื่อมต่อกับ LINE</strong>
            <div>
              1. Add Line: <IconLink size={14} /> <b>{line.officialAccount}</b>
            </div>
            <div>
              2. พิมพ์รหัส{' '}
              <button
                className="copy-code"
                onClick={() => navigator.clipboard?.writeText(line.linkCode)}
                title="คัดลอก"
              >
                <IconCopy size={13} /> <b>{line.linkCode}</b>
              </button>{' '}
              ลงกล่องข้อความ
            </div>
          </div>
        )}
      </div>

      <button className="btn-save-bar" onClick={save} disabled={busy}>
        <IconSave size={17} /> {busy ? 'กำลังบันทึก…' : saved ? 'บันทึกแล้ว' : 'บันทึก'}
      </button>

      {dialog && (
        <CredentialDialog
          kind={dialog}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); profile.reload(); }}
        />
      )}
    </>
  );
}

/** เปลี่ยนรหัสผ่าน / เปลี่ยนรหัส PIN — both are authorised by the password. */
function CredentialDialog({ kind, onClose, onDone }) {
  const isPin = kind === 'pin';
  const [form, setForm] = useState({ currentPassword: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.next !== form.confirm) {
      return setError(new Error(isPin ? 'PIN ใหม่ไม่ตรงกัน' : 'รหัสผ่านใหม่ไม่ตรงกัน'));
    }
    setBusy(true);
    setError(null);
    try {
      if (isPin) await authApi.changePin(form.currentPassword, form.next);
      else await authApi.changePassword(form.currentPassword, form.next);
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Modal title={isPin ? 'เปลี่ยนรหัส PIN' : 'เปลี่ยนรหัสผ่าน'} onClose={onClose}>
      <ErrorNote error={error} />
      <label className="field">
        <span>รหัสผ่านปัจจุบัน</span>
        <input type="password" value={form.currentPassword} onChange={set('currentPassword')} autoFocus />
      </label>
      <label className="field">
        <span>{isPin ? 'PIN ใหม่ (4-6 หลัก)' : 'รหัสผ่านใหม่ (อย่างน้อย 8 ตัวอักษร)'}</span>
        <input
          type="password"
          inputMode={isPin ? 'numeric' : undefined}
          maxLength={isPin ? 6 : undefined}
          value={form.next}
          onChange={set('next')}
        />
      </label>
      <label className="field">
        <span>{isPin ? 'ยืนยัน PIN ใหม่' : 'ยืนยันรหัสผ่านใหม่'}</span>
        <input
          type="password"
          inputMode={isPin ? 'numeric' : undefined}
          maxLength={isPin ? 6 : undefined}
          value={form.confirm}
          onChange={set('confirm')}
        />
      </label>
      <div className="modal-actions">
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={onClose} disabled={busy}>ยกเลิก</button>
        <button className="btn btn-solid" onClick={submit} disabled={busy}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}
